import studioGuideMd from './help/studio-v2-telemetry-and-flow-debug.md?raw';
import interviewflowPlaybooksMd from './help/interviewflow-use-case-playbooks.md?raw';
import extensionsMarketplaceMd from './help/extensions-and-marketplace.md?raw';
import mcpToolsMd from './help/mcp-tools-and-integrations.md?raw';
import sfApplicationAdvisorMd from './help/sf-application-advisor.md?raw';
import returnsAssistantMd from './help/returns.md?raw';
import recruitingPortalMd from './help/recruiting.md?raw';
import personalAssistantMd from './help/personal-assistant.md?raw';
import deskAgentMd from './help/desk-agent.md?raw';
import homeSearchMd from './help/home-search.md?raw';
import listingAgentMd from './help/listing-agent.md?raw';
import tenantMetadataMd from './help/tenant-metadata.md?raw';

export const slugifyHelpHeading = (value = '') => String(value)
  .toLowerCase()
  .trim()
  .replace(/[^\w\s-]/g, '')
  .replace(/\s+/g, '-');

/** Split markdown into ## sections keyed by slugified heading text. */
export function splitMarkdownByH2(markdown) {
  const sections = {};
  const chunks = String(markdown || '').split(/\n(?=## )/);
  chunks.forEach((chunk, index) => {
    if (index === 0 && !chunk.startsWith('## ')) {
      const trimmed = chunk.trim();
      if (trimmed) sections._intro = trimmed;
      return;
    }
    const match = chunk.match(/^## (.+?)(?:\r?\n|$)/);
    if (match) {
      sections[slugifyHelpHeading(match[1])] = chunk.trim();
    }
  });
  return sections;
}

const studioParts = splitMarkdownByH2(studioGuideMd);

function pickParts(parts, slugs, { includeIntro = false } = {}) {
  const blocks = [];
  if (includeIntro && parts._intro) blocks.push(parts._intro);
  slugs.forEach((slug) => {
    if (parts[slug]) blocks.push(parts[slug]);
  });
  return blocks.join('\n\n---\n\n');
}

export const STUDIO_HELP_SECTIONS = [
  {
    id: 'overview',
    title: 'Overview',
    description: 'What Studio V2 includes and where to begin.',
    path: '/studio/help/overview',
    markdown: pickParts(studioParts, ['overview', 'where-to-start'], { includeIntro: true }),
    toc: [
      { id: 'overview', label: 'Studio surfaces' },
      { id: 'where-to-start', label: 'Where to start' },
    ],
  },
  {
    id: 'use-cases',
    title: 'Use cases & examples',
    description: 'Map real work to flows and apps, with worked examples.',
    path: '/studio/help/use-cases',
    markdown: pickParts(studioParts, ['from-real-world-use-case-to-flow-and-app']),
    toc: [
      { id: 'from-real-world-use-case-to-flow-and-app', label: 'Use case → flow → app' },
      { id: 'worked-example-support-console', label: 'Support console' },
      { id: 'worked-example-permit-and-intake', label: 'Permit & intake' },
      { id: 'worked-example-candidate-screening-talent--use-case-1', label: 'Candidate screening' },
      { id: 'worked-example-recruiting-pilot-1--3-in-one-app', label: 'Recruiting pilot' },
    ],
  },
  {
    id: 'builders',
    title: 'Flows, apps & forms',
    description: 'Surface guide, bindings, VS Code, and deployment APIs.',
    path: '/studio/help/builders',
    markdown: pickParts(studioParts, ['surface-guide', 'vs-code-extension-and-deployment-apis']),
    toc: [
      { id: 'surface-guide', label: 'Surface guide' },
      { id: 'apps-studioapps', label: 'Apps' },
      { id: 'forms-studioforms', label: 'Forms' },
      { id: 'vs-code-extension-and-deployment-apis', label: 'VS Code & APIs' },
    ],
  },
  {
    id: 'recruiting',
    title: 'Hiring screening portal',
    description: 'Rubric scorecards, risk flags, interview plan, and pipeline queue.',
    path: '/studio/help/recruiting',
    markdown: recruitingPortalMd,
    toc: [
      { id: 'hiring-screening-portal', label: 'Overview' },
    ],
  },
  {
    id: 'recruiting-playbooks',
    title: 'Recruiting playbooks',
    description: 'InterviewFlow screening, scorecard, and pilot bundles.',
    path: '/studio/help/recruiting-playbooks',
    markdown: interviewflowPlaybooksMd,
    toc: [
      { id: 'interviewflow-use-case-playbooks', label: 'Playbooks index' },
      { id: '1-structured-candidate-screening', label: '#1 Screening' },
      { id: '3-post-interview-summary--scorecard', label: '#3 Scorecard' },
      { id: 'worked-example-recruiting-pilot-1--3-in-one-app', label: 'Pilot (#1 + #3)' },
      { id: 'rollout-plan', label: 'Rollout plan' },
    ],
  },
  {
    id: 'returns',
    title: 'Return & RMA assistant',
    description: 'CLAW Form→Flow→App for return policy, diagnostics, and fulfillment.',
    path: '/studio/help/returns',
    markdown: returnsAssistantMd,
    toc: [{ id: 'return--rma-assistant', label: 'Overview' }],
  },
  {
    id: 'personal-assistant',
    title: 'Personal assistant',
    description: 'CLAW email, meeting prep, task triage, and day brief.',
    path: '/studio/help/personal-assistant',
    markdown: personalAssistantMd,
    toc: [{ id: 'personal-assistant', label: 'Overview' }],
  },
  {
    id: 'desk-agent',
    title: 'Desk Continuum',
    description: 'Local CLAW desk-bridge: Arrive → Triage → Focus → Ship → Close.',
    path: '/studio/help/desk-agent',
    markdown: deskAgentMd,
    toc: [{ id: 'desk-continuum', label: 'Overview' }],
  },
  {
    id: 'home-search',
    title: 'Home search advisor',
    description: 'Buyer affordability, shortlist, and briefing (CLAW).',
    path: '/studio/help/home-search',
    markdown: homeSearchMd,
    toc: [{ id: 'home-search-advisor', label: 'Overview' }],
  },
  {
    id: 'listing-agent',
    title: 'Listing agent pipeline',
    description: 'Discover → triage → enrich → campaign blast (CLAW).',
    path: '/studio/help/listing-agent',
    markdown: listingAgentMd,
    toc: [{ id: 'listing-agent-pipeline', label: 'Overview' }],
  },
  {
    id: 'permits',
    title: 'SF application advisor',
    description: 'One App × N city packages — CLAW skill demo; configure types/fees in the jurisdiction package.',
    path: '/studio/help/permits',
    markdown: sfApplicationAdvisorMd,
    toc: [
      { id: 'sf-application-advisor--feature-guide', label: 'Overview' },
      { id: 'client-demo-focus-claw-first', label: 'CLAW first' },
      { id: 'use-case-secondary', label: 'Use case' },
      { id: 'what-you-see-layout', label: 'Layout' },
      { id: 'feature-checklist', label: 'Features' },
      { id: '1-property--project-intake', label: 'Intake form' },
      { id: '2-interactive-map-address-pick', label: 'Map' },
      { id: '3-address-validation-fills-the-right-column', label: 'Address validation' },
      { id: '4-parcel--constraints-live-sf-data', label: 'Parcel & constraints' },
      { id: '5-application-type-advise-permit-vs-planning-vs-license', label: 'Advise' },
      { id: '9-studio--claw-packaging', label: 'CLAW packaging' },
      { id: '15-multi-city-one-app--n-packages', label: 'Multi-city packages' },
      { id: '15b-where-city-specific-data-is-configured', label: 'Where to configure types' },
      { id: '16-onboard-another-city-data-not-app-code', label: 'Onboard a city' },
      { id: 'how-to-try-it', label: 'How to try' },
      { id: 'what-this-is-not', label: 'Limits' },
    ],
  },
  {
    id: 'tenant-metadata',
    title: 'Tenant & app metadata',
    description: 'How to set jurisdiction, extensions, home destination, and per-tenant hybrid demos — without code for a new tenant.',
    path: '/studio/help/tenant-metadata',
    markdown: tenantMetadataMd,
    toc: [
      { id: 'tenant--app-metadata', label: 'Overview' },
      { id: 'two-layers-do-not-confuse-them', label: 'Two layers' },
      { id: 'where-to-edit-tenant-fields-ui', label: 'Tenant Management UI' },
      { id: 'hybrid-demos-per-tenant', label: 'Hybrid demos' },
      { id: 'app-metadata-metadatainterviewflow', label: 'App metadata' },
      { id: 'related-help', label: 'Related' },
    ],
  },
  {
    id: 'extensions',
    title: 'Extensions & marketplace',
    description: 'CLAW plugins, builder, skills, app panels, sideload, bundled extensions, and unified tools.',
    path: '/studio/help/extensions',
    markdown: extensionsMarketplaceMd,
    toc: [
      { id: 'extensions-and-marketplace', label: 'Introduction' },
      { id: 'architecture-at-a-glance', label: 'Architecture' },
      { id: 'system-architecture-diagram', label: 'System diagram' },
      { id: 'runtime-sequence-when-you-run-a-flow', label: 'Runtime sequence' },
      { id: 'skill-vs-mcp-tool-vs-connector', label: 'Skill vs mcp-tool vs connector' },
      { id: 'what-is-an-extension', label: 'Concepts' },
      { id: 'bundled-extensions-reference', label: 'Bundled extensions' },
      { id: 'extensions-page-studioextensions', label: 'Extensions page' },
      { id: 'plugin-builder-tab', label: 'Plugin builder' },
      { id: 'ai-plugin-assistant-qwen', label: 'AI assistant (Qwen)' },
      { id: 'sideload-json-tab', label: 'Sideload JSON' },
      { id: 'skills-on-agent-nodes', label: 'Skills on agents' },
      { id: 'extension-only-flow-nodes', label: 'Extension flow nodes' },
      { id: 'unified-tool-catalog', label: 'Tool catalog' },
      { id: 'app-panels-from-plugins', label: 'App panels' },
      { id: 'api-reference-authenticated', label: 'API reference' },
      { id: 'for-authors-and-integrators', label: 'For authors' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
    ],
  },
  {
    id: 'mcp',
    title: 'MCP tools & integrations',
    description: 'mcp-tool nodes, flow Integrations tab, dashboard, and external clients.',
    path: '/studio/help/mcp',
    markdown: mcpToolsMd,
    toc: [
      { id: 'mcp-tools-and-integrations', label: 'Introduction' },
      { id: 'how-mcp-fits-with-extensions', label: 'MCP vs extensions' },
      { id: 'skill-mcp-tool-node-or-connector', label: 'Skill, node, or connector?' },
      { id: 'tool-sources-in-the-catalog', label: 'Tool sources' },
      { id: 'flow-editor-integrations-tab', label: 'Flow Integrations tab' },
      { id: 'the-mcp-tool-flow-node', label: 'mcp-tool node' },
      { id: 'mcp-tools-on-agent-nodes-skills', label: 'MCP + skills' },
      { id: 'mcp-dashboard', label: 'MCP Dashboard' },
      { id: 'api-reference', label: 'API reference' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
    ],
  },
  {
    id: 'debug',
    title: 'Run & debug',
    description: 'Execution, streaming, multimodal assets, validation, and metrics.',
    path: '/studio/help/debug',
    markdown: pickParts(studioParts, [
      'run-and-debug-fast',
      'multimodal-assets',
      'validation-model',
      'metrics-and-operations',
    ]),
    toc: [
      { id: 'run-and-debug-fast', label: 'Run and debug fast' },
      { id: 'multimodal-assets', label: 'Multimodal assets' },
      { id: 'validation-model', label: 'Validation model' },
      { id: 'metrics-and-operations', label: 'Metrics & ops' },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    description: 'Common issues, on-call checklist, and developer map.',
    path: '/studio/help/troubleshooting',
    markdown: pickParts(studioParts, [
      'troubleshooting-top-issues',
      'on-call-first-15-minutes',
      'developer-map',
      'more-docs',
    ]),
    toc: [
      { id: 'troubleshooting-top-issues', label: 'Top issues' },
      { id: 'on-call-first-15-minutes', label: 'On-call (15 min)' },
      { id: 'developer-map', label: 'Developer map' },
      { id: 'more-docs', label: 'More docs' },
    ],
  },
];

export const STUDIO_HELP_SECTION_BY_ID = Object.fromEntries(
  STUDIO_HELP_SECTIONS.map((s) => [s.id, s]),
);

export function resolveHelpSection(sectionId) {
  return STUDIO_HELP_SECTION_BY_ID[sectionId] || STUDIO_HELP_SECTIONS[0];
}

export function legacyHashToHelpPath(hash) {
  const h = String(hash || '').replace(/^#/, '');
  if (!h) return '/studio/help/overview';
  if (h.includes('extension') || h === 'extensions-and-marketplace') {
    return `/studio/help/extensions#${h}`;
  }
  if (h.includes('mcp') || h.includes('multimodal')) {
    return `/studio/help/mcp#${h}`;
  }
  if (['run-and-debug', 'multimodal', 'validation', 'metrics'].some((p) => h.includes(p))) {
    return `/studio/help/debug#${h}`;
  }
  if (h.includes('troubleshooting') || h.includes('on-call') || h.includes('developer-map')) {
    return `/studio/help/troubleshooting#${h}`;
  }
  if (h.includes('vs-code') || h.includes('surface') || h.includes('studioapps') || h.includes('studioforms')) {
    return `/studio/help/builders#${h}`;
  }
  if (h.includes('interviewflow') || h.includes('scorecard') || h.includes('screening') || h.includes('rollout')) {
    return `/studio/help/recruiting#${h}`;
  }
  if (h.includes('permit') || h.includes('parcel') || h.includes('sf-application') || h.includes('zoning')) {
    return `/studio/help/permits#${h}`;
  }
  if (h.includes('use-case') || h.includes('worked-example')) {
    return `/studio/help/use-cases#${h}`;
  }
  return `/studio/help/overview#${h}`;
}

export const STUDIO_HELP_NAV_ITEMS = STUDIO_HELP_SECTIONS.map((s) => ({
  id: `studio-help-${s.id}`,
  label: s.title,
  path: s.path,
  permission: 'agents',
}));
